import { LogoRemondisBlack } from 'assets/logotype';
import { IMaterial, ISavedMaterial } from 'components/MaterialSelector';
import { IPlaces } from 'components/SearchAddressInput';
import { ISegment } from 'components/Segments';
import { Materials, SEGMENT_TITLES } from 'data';
import useContactForm from 'hooks/useContactForm';
import { createContext, Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AddressSection from 'sections/AddressSection';
import ContactSection from 'sections/ContactSection';
import MaterialSection from 'sections/MaterialSection';
import SegmentSection from 'sections/SegmentSection';
import WorkplaceSection, { IWorkplace } from 'sections/WorkplaceSection';
import styled from 'styled-components';
import { theme } from 'styles/theme';

// Custom arrow icons
const ArrowRight = (props: any) => (
  <svg width="49" height="63" viewBox="0 0 49 63" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M28.4662 30.4453C28.7643 30.755 28.7643 31.245 28.4662 31.5547L22.8765 37.3638C22.3771 37.8827 21.5 37.5292 21.5 36.8091L21.5 25.1909C21.5 24.4708 22.3771 24.1173 22.8765 24.6362L28.4662 30.4453Z"
      fill="#393A41"
    />
  </svg>
);

const ArrowLeft = (props: any) => (
  <svg width="49" height="63" viewBox="0 0 49 63" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M19.5337 31.5547C19.2357 31.245 19.2357 30.755 19.5337 30.4453L25.1235 24.6362C25.6229 24.1173 26.5 24.4708 26.5 25.1909V36.8091C26.5 37.5292 25.6229 37.8827 25.1235 37.3638L19.5337 31.5547Z"
      fill="#393A41"
    />
  </svg>
);

interface ValidationErrorType {
  address: string;
  material: string;
  workplaceName: string;
}

interface AddressContextType {
  address: IPlaces['fullAddress'];
  setAddress: Dispatch<SetStateAction<IPlaces['fullAddress']>>;
  addressValidationError: string;
  setStepValidation: Dispatch<SetStateAction<ValidationErrorType>>;
}

export const AddressContext = createContext<AddressContextType | null>(null);

const Offer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const landingSlug =
    (location.state as { landingSlug?: string } | null)?.landingSlug ?? 'alectafastigheter';

  const [address, setAddress] = useState<IPlaces['fullAddress']>('');
  const [segments, setSegments] = useState<ISegment[]>(() =>
    SEGMENT_TITLES.map((title) => ({ title, selected: false }))
  );

  const [stepValidation, setStepValidation] = useState<ValidationErrorType>({
    address: '',
    material: '',
    workplaceName: '',
  });

  const [selectedSegment, setSelectedSegment] = useState('');
  const [materials, setMaterials] = useState<IMaterial[]>(() =>
    Materials.map((m) => ({
      ...m,
      selected: false,
    }))
  );
  const [savedMaterials, setSavedMaterials] = useState<ISavedMaterial[]>([]);
  const [otherWaste, setOtherWaste] = useState<string>('');
  const [workplace, setWorkplace] = useState<IWorkplace>({
    name: '',
    size: 0,
    headcount: 0,
    apartments: 0,
    coLocatedBusinesses: 0,
    otherBusinessDescription: '',
  });
  const { contact, contactErrors, handleContactChange, validateContactForm } = useContactForm();

  const stepValidationErrors = {
    address: 'Ange en adress från listan',
    material:
      selectedSegment === 'Annat'
        ? 'Ange vilken typ av matrial ni behöver hjälp att återvinna'
        : 'Du måste välja minst ett material eller fylla i annat avfall',
    workplaceName: 'Ange ett namn',
  };

  useEffect(() => {
    const selectedMaterials = materials.filter((material) => material.selected);

    const newMaterials: ISavedMaterial[] = selectedMaterials.map((material) => ({
      type: material.type,
    }));

    if (otherWaste.trim()) {
      newMaterials.push({
        type: 'Annat avfall',
        description: otherWaste.trim(),
      });
    }

    setSavedMaterials(newMaterials);
  }, [materials, otherWaste]);

  const OFFER_DATA = {
    address: address,
    contact: {
      name: contact.name,
      company: contact.company,
      email: contact.email,
      phone: contact.phone,
      other: contact.other.trim(),
    },
    segment: selectedSegment,
    material: savedMaterials,
    workplace: {
      name: workplace.name,
      size: workplace.size > 0 ? `ca ${workplace.size} m²` : '',
      headcount: workplace.headcount > 0 ? `ca ${workplace.headcount} st` : '',
      apartments:
        selectedSegment === 'Bostadsfastighet' && workplace.apartments > 0
          ? `${workplace.apartments} st`
          : '',
      co_located_businesses:
        selectedSegment === 'Bostadsfastighet' ? `${workplace.coLocatedBusinesses || 0} st` : '',
      other_business_description:
        selectedSegment === 'Annat' ? workplace.otherBusinessDescription : '',
    },
  };

  const onPostOffer = async () => {
    const URL = 'https://api.rmdse.se/e-portal/v2/public/tickets';

    try {
      const response = await fetch(URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(OFFER_DATA),
      });

      if (!response.ok) {
        throw new Error(`Response status: ${response.status}`);
      }

      await response.json();
    } catch (error) {}
  };

  const handleSubmit = () => {
    const isContactValid = validateContactForm();
    if (!isContactValid) {
      return;
    }

    if (!address) {
      setStepValidation((prev) => ({
        ...prev,
        address: stepValidationErrors.address,
      }));
      return;
    }

    const materialIsValid =
      selectedSegment === 'Annat'
        ? otherWaste.trim().length > 0
        : otherWaste.trim().length > 0 || !!materials.find((material) => material.selected);

    if (!materialIsValid) {
      setStepValidation((prev) => ({
        ...prev,
        material: stepValidationErrors.material,
      }));
      return;
    }

    if (
      selectedSegment !== 'Kontorsåtervinning' &&
      selectedSegment !== 'Bostadsfastighet' &&
      selectedSegment !== 'Annat' &&
      !workplace.name.trim()
    ) {
      setStepValidation((prev) => ({
        ...prev,
        workplaceName: stepValidationErrors.workplaceName,
      }));
      return;
    }

    if (selectedSegment === 'Bostadsfastighet' && workplace.apartments <= 0) {
      return;
    }

    if (selectedSegment === 'Annat' && !workplace.otherBusinessDescription.trim()) {
      return;
    }

    onPostOffer();
    navigate('/thanks', { state: { from: '/', landingSlug } });
  };

  const [currentStepIndex, setCurrentStepIndex] = useState(1);

  const steps = [
    {
      id: 'address',
      step: 1,
      title: 'Adress',
      description: 'Var behöver ni hjälp med återvinning?',
    },
    { id: 'segment', step: 2, title: 'Segment', description: 'Välj er bransch' },
    { id: 'material', step: 3, title: 'Material', description: 'Välj material att återvinna' },
    {
      id: 'workplace',
      step: 4,
      title: 'Arbetsplats',
      description: 'Information om er arbetsplats',
    },
    { id: 'contact', step: 5, title: 'Kontakt', description: 'Kontaktinformation' },
  ];

  const goToNextStep = () => {
    if (currentStepIndex < steps.length) {
      setCurrentStepIndex(currentStepIndex + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousStep = () => {
    if (currentStepIndex > 1) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  const canGoToNextStep = () => {
    switch (currentStepIndex) {
      case 1: // Address
        return !!address;
      case 2: // Segment
        return !!selectedSegment;
      case 3: // Material
        return selectedSegment === 'Annat'
          ? otherWaste.trim().length > 0
          : otherWaste.trim().length > 0 || materials.find((material) => material.selected);
      case 4: // Workplace
        if (selectedSegment === 'Kontorsåtervinning') {
          return true;
        }
        if (selectedSegment === 'Bostadsfastighet') {
          return workplace.apartments > 0;
        }
        if (selectedSegment === 'Annat') {
          return !!workplace.otherBusinessDescription.trim();
        }
        return !!workplace.name.trim();
      case 5: // Contact
        return true; // Will be validated on submit
      default:
        return false;
    }
  };

  const canGoToPreviousStep = () => {
    return currentStepIndex > 1;
  };

  useEffect(() => {
    const hasRequiredMaterial =
      selectedSegment === 'Annat'
        ? otherWaste.trim().length > 0
        : !!otherWaste.trim() || !!materials.find((material) => material.selected);

    if (hasRequiredMaterial) {
      setStepValidation((prev) => ({
        ...prev,
        material: '',
      }));
    } else {
      setStepValidation((prev) => ({
        ...prev,
        material: stepValidationErrors.material,
      }));
    }
  }, [materials, otherWaste, selectedSegment, stepValidationErrors.material]);

  const renderCurrentStep = () => {
    switch (currentStepIndex) {
      case 1:
        return <AddressSection onClick={goToNextStep} />;
      case 2:
        return (
          <SegmentSection
            handleSegmentClick={(index) => {
              setSegments((prevSegments) =>
                prevSegments.map((segment, i) => ({
                  ...segment,
                  selected: i === index,
                }))
              );

              setWorkplace({
                name: '',
                size: 0,
                headcount: 0,
                apartments: 0,
                coLocatedBusinesses: 0,
                otherBusinessDescription: '',
              });

              const segmentTitle = segments[index].title;
              setSelectedSegment(segmentTitle);
              setMaterials(
                Materials.map((m) => ({
                  ...m,
                  selected: m.preselectedForSegments?.includes(segmentTitle) ?? false,
                }))
              );
            }}
            data={segments}
            disabled={!selectedSegment}
            onClick={goToNextStep}
          />
        );
      case 3:
        return (
          <MaterialSection
            data={materials}
            selectedSegment={selectedSegment}
            onSelectionChange={(index, newValue) => {
              setMaterials((prevMaterials) =>
                prevMaterials.map((item, i) =>
                  i === index ? { ...item, selected: !newValue } : item
                )
              );
            }}
            otherWasteValue={otherWaste}
            validationError={stepValidation.material}
            onChangeOtherWaste={setOtherWaste}
            disabled={
              selectedSegment === 'Annat'
                ? !otherWaste.trim().length
                : !otherWaste.trim().length && !materials.find((material) => material.selected)
            }
            onClick={goToNextStep}
          />
        );
      case 4:
        return (
          <WorkplaceSection
            selectedSegment={selectedSegment}
            workplace={workplace}
            validationError={stepValidation.workplaceName}
            onChange={(key: keyof IWorkplace, value: string | number) => {
              setWorkplace((prevWorkplace) => ({
                ...prevWorkplace,
                [key]: value,
              }));

              if (value.toString().trim()) {
                setStepValidation((prev) => ({
                  ...prev,
                  workplaceName: '',
                }));
              } else {
                setStepValidation((prev) => ({
                  ...prev,
                  workplaceName: stepValidationErrors.workplaceName,
                }));
              }
            }}
            onClick={goToNextStep}
          />
        );
      case 5:
        return (
          <ContactSection
            onSubmit={handleSubmit}
            data={contact}
            onChange={handleContactChange}
            errors={contactErrors}
          />
        );
      default:
        return null;
    }
  };

  return (
    <AddressContext.Provider
      value={{
        address,
        setAddress,
        addressValidationError: stepValidation.address,
        setStepValidation,
      }}
    >
      <Container>
        <TopBar>
          <LogoContainer>
            <Logo src={LogoRemondisBlack} alt="REMONDIS logo" />
          </LogoContainer>

          <CenterSection>
            <ProgressContainer>
              <ProgressBar>
                <ProgressFill
                  $progress={Math.max(0, ((currentStepIndex - 1) / (steps.length - 1)) * 100)}
                />
              </ProgressBar>
            </ProgressContainer>

            <NavigationContainer>
              <NavigationButton
                onClick={goToPreviousStep}
                disabled={!canGoToPreviousStep()}
                $isVisible={canGoToPreviousStep()}
              >
                <ArrowLeft />
              </NavigationButton>

              <StepIndicator>
                {currentStepIndex} / {steps.length}
              </StepIndicator>

              <NavigationButton
                onClick={goToNextStep}
                disabled={!canGoToNextStep()}
                $isVisible={currentStepIndex < steps.length}
                $isPrimary
              >
                <ArrowRight />
              </NavigationButton>
            </NavigationContainer>
          </CenterSection>

          <div></div>
        </TopBar>

        <StepContent>{renderCurrentStep()}</StepContent>
      </Container>
    </AddressContext.Provider>
  );
};

export default Offer;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  position: relative;
  background: ${theme.colors.background};

  @media (max-height: 500px) {
    min-height: 100vh;
  }
`;

const TopBar = styled.div`
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  background: ${theme.colors.background};
  z-index: 100;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 2rem;
  padding: 1rem 1.5rem;

  @media (max-width: 768px) {
    gap: 1.5rem;
    padding: 0.75rem 1rem;
    grid-template-columns: 1fr;
  }

  @media (max-height: 600px) {
    padding: 0.75rem 1.25rem;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
`;

const Logo = styled.img`
  height: 45px;
  width: auto;

  @media (max-height: 600px) {
    height: 38px;
  }

  @media (max-width: 768px) {
    height: 40px;
  }
`;

const CenterSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-self: center;
  width: 100%;

  @media (max-width: 768px) {
    gap: 0.75rem;
  }

  @media (max-height: 600px) {
    gap: 0.5rem;
  }
`;

const ProgressContainer = styled.div`
  width: 100%;
  max-width: 1000px;
  min-width: 600px;
  height: 12px;
  background: #f8f8f8;
  border-radius: 8px;
  overflow: hidden;

  @media (max-width: 768px) {
    min-width: 100%;
  }

  @media (max-height: 600px) {
    height: 10px;
  }
`;

const ProgressBar = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
`;

const ProgressFill = styled.div<{ $progress: number }>`
  height: 100%;
  background: #b40b1a;
  border-radius: 4px;
  width: ${({ $progress }) => `${$progress}%`};
  transition: width 0.5s ease-in-out;
  min-width: 0;
`;

const StepContent = styled.div`
  flex: 1;
  overflow: visible;
  position: relative;
  display: flex;
  align-items: stretch;
  min-height: calc(100vh - 107px);

  @media (max-height: 600px) {
    min-height: calc(100vh - 92px);
  }

  @media (max-height: 500px) {
    min-height: calc(100vh - 82px);
  }

  & > * {
    width: 100%;
    max-height: 100%;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: #a8a8a8;
    }
  }
`;

const NavigationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    gap: 0.75rem;
  }

  @media (max-height: 600px) {
    gap: 0.5rem;
  }
`;

const NavigationButton = styled.button<{ $isVisible: boolean; $isPrimary?: boolean }>`
  padding: 0;
  border: none;
  background: transparent;
  color: ${({ $isPrimary }) => ($isPrimary ? '#b40b1a' : '#393A41')};
  width: 49px;
  height: 63px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ $isVisible }) => ($isVisible ? 'pointer' : 'not-allowed')};
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0.3)};
  transition: all 0.3s ease;

  &:hover {
    ${({ $isVisible, $isPrimary }) =>
      $isVisible &&
      `
      transform: scale(1.05);
      color: ${$isPrimary ? '#8a0915' : '#666'};
    `}
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.3;
  }

  svg {
    width: 100%;
    height: 100%;
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 50px;
  }

  @media (max-height: 600px) {
    width: 35px;
    height: 45px;
  }
`;

const StepIndicator = styled.div`
  font-size: 0.875rem;
  color: #666;
  font-weight: 500;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 0.75rem;
  }

  @media (max-height: 600px) {
    font-size: 0.75rem;
  }
`;
